import React, { Fragment } from "react";

import img13 from "../../../images/service/kycAml/kyc-aml.jpg";
import img14 from "../../../images/service/kycAml/kyc-aml2.png";
import img15 from "../../../images/service/kycAml/kyc-aml3.jpg";

import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import TheorySection from "../../../components/PageSection/Theory";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";

// --------------------------------------------------
// DATA
// --------------------------------------------------

const serviceCards = [
  {
    icon: sIcon1,
    title: "KYC Verification Solutions",
    content:
      "Robust identity verification systems including document verification, facial recognition, and liveness checks.",
  },
  {
    icon: sIcon2,
    title: "AML Compliance Systems",
    content:
      "Advanced anti-money laundering solutions for transaction monitoring and risk analysis.",
  },
  {
    icon: sIcon3,
    title: "Risk Assessment & Monitoring",
    content:
      "Real-time user risk scoring and continuous monitoring to detect suspicious behavior.",
  },
];

const benefits = [
  {
    title: "Regulatory Compliance",
    description:
      "Ensure adherence to global financial regulations and compliance standards.",
    icon: sIcon1,
  },
  {
    title: "Fraud Prevention",
    description:
      "Detect and prevent identity fraud, money laundering, and illicit activities.",
    icon: sIcon2,
  },
  {
    title: "Enhanced Trust",
    description:
      "Build user confidence through transparent and secure verification processes.",
    icon: sIcon3,
  },
  {
    title: "Operational Efficiency",
    description:
      "Automated verification reduces manual effort and onboarding time.",
    icon: sIcon4,
  },
  {
    title: "Scalable Architecture",
    description:
      "Designed to handle millions of verifications without performance loss.",
    icon: sIcon5,
  },
  {
    title: "Global Coverage",
    description:
      "Supports identity verification across multiple countries and regions.",
    icon: sIcon6,
  },
];

const faqs = [
  {
    q: "What is KYC?",
    a: "Know Your Customer (KYC) is the process of verifying the identity of users to prevent fraud and ensure regulatory compliance.",
  },
  {
    q: "Why is AML important?",
    a: "Anti-Money Laundering (AML) helps detect and prevent illegal financial activities such as money laundering and terrorism financing.",
  },
  {
    q: "Can KYC & AML be integrated into crypto platforms?",
    a: "Yes. Our solutions integrate seamlessly with exchanges, wallets, DeFi platforms, and fintech applications.",
  },
  {
    q: "Do you support global regulations?",
    a: "Yes. Our systems are designed to align with international compliance frameworks.",
  },
];

// --------------------------------------------------
// PAGE COMPONENT
// --------------------------------------------------

export default function KycAmlServices() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="KYC & AML Services"
        pagesub="Secure, compliant, and intelligent identity verification solutions"
      />

      {/* ================= HERO ================= */}
      <HeroSection
        title={
          <>
            Enterprise-Grade{" "}
            <span className="gradient-text">KYC & AML Compliance</span>
          </>
        }
        subtitle={`Our KYC and AML solutions help businesses verify user identities,
detect financial risks, and stay compliant with global regulatory standards.
Protect your platform while building user trust.`}
        buttonText="Get Compliance Solution"
        buttonLink="/contact"
        image={img13}
        imageAlt="KYC AML Services"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Understanding KYC & AML Compliance"
        image={img14}
        paragraphs={[
          "Know Your Customer (KYC) and Anti-Money Laundering (AML) are essential regulatory practices designed to prevent identity fraud, financial crimes, and illegal fund movements. These processes ensure that businesses verify user identities before granting access to financial services.",
          "In the digital economy, automated KYC and AML systems are critical for platforms handling sensitive financial transactions. By implementing structured verification and monitoring workflows, businesses can reduce risks while ensuring transparency and accountability.",
        ]}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Why KYC & AML Are Crucial for Digital Platforms"
        image={img15}
        paragraphs={[
          "Crypto exchanges, wallets, fintech platforms, and digital payment systems operate in high-risk environments where regulatory scrutiny is increasing. Without proper compliance mechanisms, platforms may face penalties, operational disruptions, or loss of user trust.",
          "Our KYC & AML solutions are designed to automate compliance processes, detect suspicious activity in real time, and provide audit-ready records. This ensures smooth operations while meeting regulatory expectations across jurisdictions.",
        ]}
      />

      {/* ================= SERVICES ================= */}
      <ServiceCardsSection
        title="Our KYC & AML Services"
        cards={serviceCards}
      />

      {/* ================= BENEFITS ================= */}
      <Benefit
        title="Benefits of KYC & AML Solutions"
        benefits={benefits}
      />

      {/* ================= KEY FEATURES ================= */}
      <KeyFeaturesSection
        title="Key Features of Our Compliance Platform"
        subtitle="Designed for security, accuracy, and regulatory readiness."
        features={[
          {
            icon: sIcon1,
            title: "Identity Verification",
            description:
              "Document verification, biometric checks, and liveness detection.",
          },
          {
            icon: sIcon2,
            title: "Transaction Monitoring",
            description:
              "Continuous monitoring of transactions for suspicious activity.",
          },
          {
            icon: sIcon3,
            title: "Risk Scoring",
            description:
              "AI-powered user risk profiling and scoring.",
          },
          {
            icon: sIcon4,
            title: "Global Compliance",
            description:
              "Support for regional and international regulatory standards.",
          },
          {
            icon: sIcon5,
            title: "Audit-Ready Reports",
            description:
              "Detailed logs and compliance reports for audits.",
          },
          {
            icon: sIcon6,
            title: "API Integration",
            description:
              "Seamless integration with existing platforms via APIs.",
          },
        ]}
      />

      {/* ================= WHY CHOOSE US ================= */}
      <WhyChooseUsSection
        items={[
          "Regulatory-Focused Architecture",
          "AI-Powered Risk Detection",
          "End-to-End Compliance Support",
        ]}
      />

      {/* ================= FAQ ================= */}
      <FaqSection faqs={faqs} />

      {/* ================= CTA ================= */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
